/**
 * AttributionManager - Comprehensive attribution tracking system
 * Captures and persists all marketing attribution data across sessions.
 *
 * Standalone version for co-aurora (no build system).
 * Ported from shopper/src/scripts/lib/attribution.js
 */
(function() {
  'use strict';

  /* Avoid double-initialization */
  if (window.AttributionManager) return;

  var STORAGE_PREFIX = 'attr_';
  var EXPIRY_DAYS = 28;

  function AttributionManager() {
    this.STORAGE_PREFIX = STORAGE_PREFIX;
    this.EXPIRY_DAYS = EXPIRY_DAYS;
    this.initialized = false;
    this.ipFetchPromise = null;

    /* Attribution data structure */
    this.attribution = {
      clickIds: {
        gclid: null,
        gbraid: null,
        wbraid: null,
        fbclid: null,
        ttclid: null,
        msclkid: null,
        twclid: null,
        li_fat_id: null,
        pclid: null,
        sclid: null
      },
      utm: {
        source: null,
        medium: null,
        campaign: null,
        term: null,
        content: null,
        id: null
      },
      session: {
        landing_page: null,
        landing_page_timestamp: null,
        referrer: null,
        ga_client_id: null,
        ga_session_id: null,
        fb_browser_id: null,
        fb_click_id: null,
        client_ip: null,
        is_ipv6: null,
        ip_fetched_at: null
      },
      customer: {
        email: null,
        phone: null,
        first_name: null,
        last_name: null,
        city: null,
        state: null,
        country: null,
        zip: null
      }
    };

    this.init();
  }

  AttributionManager.prototype.init = function() {
    if (this.initialized) return;

    this.loadFromStorage();
    this.capturePageAttribution();
    this.setupFormListeners();
    this.syncCookies();

    var self = this;
    this.ipFetchPromise = this.fetchAndStoreIP();
    this.ipFetchPromise.catch(function() {
      setTimeout(function() {
        self.ipFetchPromise = self.fetchAndStoreIP();
      }, 2000);
    });

    this.initialized = true;
  };

  /* ---- IP fetching ---- */

  AttributionManager.prototype.fetchAndStoreIP = function() {
    var self = this;
    return new Promise(function(resolve, reject) {
      /* Check cache (30 min) */
      if (self.attribution.session.client_ip && self.attribution.session.ip_fetched_at) {
        var age = Date.now() - self.attribution.session.ip_fetched_at;
        if (age < 30 * 60 * 1000) {
          resolve(self.attribution.session.client_ip);
          return;
        }
      }

      var baseEndpoint = window.theme && window.theme.analytics && window.theme.analytics.lambdaEndpoint;
      if (!baseEndpoint) {
        resolve(null);
        return;
      }

      var ipEndpoint = baseEndpoint + 'prod/ip';

      fetch(ipEndpoint, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      })
        .then(function(response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.json();
        })
        .then(function(data) {
          if (data.ip) {
            self.attribution.session.client_ip = data.ip;
            self.attribution.session.is_ipv6 = data.ipv6;
            self.attribution.session.ip_fetched_at = Date.now();
            self.saveToStorage();
            resolve(data.ip);
          } else {
            resolve(null);
          }
        })
        .catch(function(err) {
          reject(err);
        });
    });
  };

  AttributionManager.prototype.ensureIPAvailable = function(timeout) {
    timeout = timeout || 5000;
    var self = this;

    return new Promise(function(resolve) {
      if (self.attribution.session.client_ip) {
        resolve(self.attribution.session.client_ip);
        return;
      }

      if (self.ipFetchPromise) {
        var timer = setTimeout(function() { resolve(null); }, timeout);
        self.ipFetchPromise.then(function(ip) {
          clearTimeout(timer);
          resolve(ip);
        }).catch(function() {
          clearTimeout(timer);
          resolve(null);
        });
        return;
      }

      self.ipFetchPromise = self.fetchAndStoreIP();
      var timer2 = setTimeout(function() { resolve(null); }, timeout);
      self.ipFetchPromise.then(function(ip) {
        clearTimeout(timer2);
        resolve(ip);
      }).catch(function() {
        clearTimeout(timer2);
        resolve(null);
      });
    });
  };

  /* ---- Page attribution ---- */

  AttributionManager.prototype.capturePageAttribution = function() {
    var params = new URLSearchParams(window.location.search);
    var self = this;

    /* Click IDs */
    Object.keys(this.attribution.clickIds).forEach(function(key) {
      var value = params.get(key);
      if (value) {
        self.attribution.clickIds[key] = value;
        if (key === 'fbclid') {
          var fbcValue = 'fb.1.' + Date.now() + '.' + value;
          self.attribution.session.fb_click_id = fbcValue;
          self.setCookie('_fbc', fbcValue, 90);
        }
      }
    });

    /* UTM params */
    Object.keys(this.attribution.utm).forEach(function(key) {
      var paramName = key === 'id' ? 'utm_id' : 'utm_' + key;
      var value = params.get(paramName);
      if (value) {
        self.attribution.utm[key] = value;
      }
    });

    /* Landing page (new sessions only) */
    if (!this.attribution.session.landing_page || this.isNewSession()) {
      this.attribution.session.landing_page = window.location.href;
      this.attribution.session.landing_page_timestamp = Date.now();
      this.attribution.session.referrer = document.referrer;
    }

    this.saveToStorage();
  };

  AttributionManager.prototype.isNewSession = function() {
    var lastTimestamp = this.attribution.session.landing_page_timestamp;
    if (!lastTimestamp) return true;
    return (Date.now() - lastTimestamp) > 30 * 60 * 1000;
  };

  /* ---- Cookie sync ---- */

  AttributionManager.prototype.syncCookies = function() {
    /* GA */
    var gaCookie = this.getCookie('_ga');
    if (gaCookie) {
      var parts = gaCookie.split('.');
      if (parts.length >= 4) {
        this.attribution.session.ga_client_id = parts[2] + '.' + parts[3];
        try { localStorage.setItem('_ga_client_id', this.attribution.session.ga_client_id); } catch (e) { /* noop */ }
      }
    }

    /* Facebook */
    var fbpCookie = this.getCookie('_fbp');
    if (fbpCookie) this.attribution.session.fb_browser_id = fbpCookie;

    var fbcCookie = this.getCookie('_fbc');
    if (fbcCookie) this.attribution.session.fb_click_id = fbcCookie;
  };

  /* Alias used by Meta integration */
  AttributionManager.prototype.collectCookieData = AttributionManager.prototype.syncCookies;

  /* ---- Form listeners ---- */

  AttributionManager.prototype.setupFormListeners = function() {
    var self = this;
    var debounceTimer;

    var fieldMap = {
      'email': 'email',
      'customer[email]': 'email',
      'contact[email]': 'email',
      'phone': 'phone',
      'customer[phone]': 'phone',
      'contact[phone]': 'phone',
      'first_name': 'first_name',
      'customer[first_name]': 'first_name',
      'last_name': 'last_name',
      'customer[last_name]': 'last_name',
      'city': 'city',
      'address[city]': 'city',
      'province': 'state',
      'state': 'state',
      'address[province]': 'state',
      'country': 'country',
      'address[country]': 'country',
      'zip': 'zip',
      'postal_code': 'zip',
      'address[zip]': 'zip'
    };

    document.addEventListener('change', function(e) {
      if (!e.target.matches('input, select, textarea')) return;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function() {
        var input = e.target;
        var name = (input.name || input.id || '').toLowerCase();
        var value = input.value;
        if (!name || !value) return;

        var mapped = fieldMap[name];
        if (mapped && self.attribution.customer.hasOwnProperty(mapped)) {
          self.attribution.customer[mapped] = value;
          self.saveToStorage();
        }
      }, 500);
    });
  };

  /* ---- Getters ---- */

  AttributionManager.prototype.getAttribution = function() { return this.attribution; };
  AttributionManager.prototype.getClickIds = function() { return this.attribution.clickIds; };
  AttributionManager.prototype.getUTMParams = function() { return this.attribution.utm; };
  AttributionManager.prototype.getSessionData = function() { return this.attribution.session; };
  AttributionManager.prototype.getCustomerData = function() { return this.attribution.customer; };

  AttributionManager.prototype.getGoogleData = function() {
    return {
      gclid: this.attribution.clickIds.gclid,
      gbraid: this.attribution.clickIds.gbraid,
      wbraid: this.attribution.clickIds.wbraid,
      client_id: this.attribution.session.ga_client_id,
      session_id: this.attribution.session.ga_session_id,
      source: this.attribution.utm.source,
      medium: this.attribution.utm.medium,
      campaign: this.attribution.utm.campaign,
      term: this.attribution.utm.term,
      content: this.attribution.utm.content,
      id: this.attribution.utm.id
    };
  };

  AttributionManager.prototype.getMetaData = function() {
    var result = {
      fbclid: this.attribution.clickIds.fbclid,
      fbc: this.attribution.session.fb_click_id,
      fbp: this.attribution.session.fb_browser_id
    };
    var c = this.attribution.customer;
    if (c.email) result.email = c.email;
    if (c.phone) result.phone = c.phone;
    if (c.first_name) result.first_name = c.first_name;
    if (c.last_name) result.last_name = c.last_name;
    if (c.city) result.city = c.city;
    if (c.state) result.state = c.state;
    if (c.country) result.country = c.country;
    if (c.zip) result.zip = c.zip;
    return result;
  };

  AttributionManager.prototype.getTikTokData = function() {
    return {
      ttclid: this.attribution.clickIds.ttclid,
      source: this.attribution.utm.source,
      medium: this.attribution.utm.medium,
      campaign: this.attribution.utm.campaign
    };
  };

  /* ---- Storage ---- */

  AttributionManager.prototype.saveToStorage = function() {
    if (!this.isStorageAvailable()) return;
    var expiry = Date.now() + (this.EXPIRY_DAYS * 24 * 60 * 60 * 1000);
    var prefix = this.STORAGE_PREFIX;

    try {
      localStorage.setItem(prefix + 'clickids', JSON.stringify({ data: this.attribution.clickIds, expiry: expiry }));
      localStorage.setItem(prefix + 'utm', JSON.stringify({ data: this.attribution.utm, expiry: expiry }));
      localStorage.setItem(prefix + 'session', JSON.stringify({ data: this.attribution.session, expiry: expiry }));
      localStorage.setItem(prefix + 'customer', JSON.stringify({ data: this.attribution.customer, expiry: expiry }));
    } catch (e) { /* quota exceeded */ }

    this.syncToCart();
  };

  AttributionManager.prototype.loadFromStorage = function() {
    if (!this.isStorageAvailable()) return;
    var self = this;
    var sections = ['clickids', 'utm', 'session', 'customer'];

    sections.forEach(function(section) {
      try {
        var stored = localStorage.getItem(self.STORAGE_PREFIX + section);
        if (!stored) return;
        var parsed = JSON.parse(stored);
        if (parsed.expiry && parsed.expiry > Date.now()) {
          var target = section === 'clickids' ? 'clickIds' : section;
          Object.keys(parsed.data).forEach(function(key) {
            if (parsed.data[key] && !self.attribution[target][key]) {
              self.attribution[target][key] = parsed.data[key];
            }
          });
        } else {
          localStorage.removeItem(self.STORAGE_PREFIX + section);
        }
      } catch (e) { /* ignore */ }
    });
  };

  AttributionManager.prototype.clearStorage = function() {
    if (!this.isStorageAvailable()) return;
    var prefix = this.STORAGE_PREFIX;
    ['clickids', 'utm', 'session', 'customer'].forEach(function(s) {
      localStorage.removeItem(prefix + s);
    });
  };

  /* ---- Utilities ---- */

  AttributionManager.prototype.isStorageAvailable = function() {
    try {
      var t = '__attr_test__';
      localStorage.setItem(t, t);
      localStorage.removeItem(t);
      return true;
    } catch (e) { return false; }
  };

  AttributionManager.prototype.getCookie = function(name) {
    var value = '; ' + document.cookie;
    var parts = value.split('; ' + name + '=');
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  AttributionManager.prototype.setCookie = function(name, value, days) {
    var expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = name + '=' + value + ';expires=' + expires.toUTCString() + ';path=/;SameSite=Lax';
  };

  /* ---- Cart sync ---- */

  AttributionManager.prototype.syncToCart = function() {
    var compactData = {};
    var self = this;

    Object.keys(this.attribution.clickIds).forEach(function(key) {
      if (self.attribution.clickIds[key]) compactData[key] = self.attribution.clickIds[key];
    });
    Object.keys(this.attribution.utm).forEach(function(key) {
      if (self.attribution.utm[key]) compactData['utm_' + key] = self.attribution.utm[key];
    });

    var sess = this.attribution.session;
    if (sess.client_ip) { compactData.client_ip = sess.client_ip; compactData.is_ipv6 = sess.is_ipv6; }
    if (sess.ga_client_id) compactData.ga_client_id = sess.ga_client_id;
    if (sess.ga_session_id) compactData.ga_session_id = sess.ga_session_id;
    if (sess.fb_browser_id) compactData.fb_browser_id = sess.fb_browser_id;
    if (sess.fb_click_id) compactData.fb_click_id = sess.fb_click_id;
    if (sess.landing_page) compactData.landing_page = sess.landing_page;
    if (sess.referrer) compactData.referrer = sess.referrer;

    Object.keys(this.attribution.customer).forEach(function(key) {
      if (self.attribution.customer[key]) compactData['customer_' + key] = self.attribution.customer[key];
    });

    if (Object.keys(compactData).length === 0) return;

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ attributes: { attribution_data: JSON.stringify(compactData) } })
    }).catch(function() { /* best effort */ });
  };

  /* Create singleton */
  window.AttributionManager = new AttributionManager();
})();
